import { useTheme } from '@mui/material/styles';
import dynamic from 'next/dynamic';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Props as JoyrideProps, EVENTS } from 'react-joyride';
import * as Sentry from '@sentry/nextjs';

const Joyride = dynamic(() => import('react-joyride'), { ssr: false });

export interface TourGuidePProviderContext {
  enqueueTourGuide: (
    config: Omit<JoyrideProps, 'run'> & {
      /**
       * @description Set this value if you want tour guide only show once
       */
      tourGuideId?: string;
    },
  ) => void;
}

const tourGuideContext = React.createContext<TourGuidePProviderContext>({} as any);

interface Props {}

const DEFAULT_PROPS: JoyrideProps = { steps: [] };

const historyTourGuide: Record<string, boolean> = (() => {
  if (typeof window !== 'undefined') {
    try {
      return JSON.parse(localStorage.getItem('SHOWED_TOUR_GUIDE') || '{}');
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  }
  return {};
})();

const TourGuideProvider: React.FC<React.PropsWithChildren<Props>> = ({ children }) => {
  const [queue, setQueue] = useState<JoyrideProps[]>([]);
  const [run, setRun] = useState(false);
  const theme = useTheme();

  const enqueueTourGuide = useCallback<TourGuidePProviderContext['enqueueTourGuide']>(
    ({ tourGuideId, ...config }) => {
      if (tourGuideId && historyTourGuide[tourGuideId]) {
        // If already show, do nothing
        return;
      }
      setQueue((currentQueue) => [...currentQueue, config]);
      if (tourGuideId) {
        historyTourGuide[tourGuideId] = true;
        localStorage.setItem('SHOWED_TOUR_GUIDE', JSON.stringify(historyTourGuide));
      }
    },
    [],
  );

  const joyrideProps = useMemo(() => {
    if (queue.length) {
      return queue[0];
    }
    return DEFAULT_PROPS;
  }, [queue]);

  useEffect(() => {
    // Trigger re-run again by toggle the "run" value
    setRun(false);
    // Only set run = true if queue is not empty, DEFAULT_PROPS mean queue is empty
    if (joyrideProps !== DEFAULT_PROPS) {
      requestAnimationFrame(() => {
        setRun(true);
      });
    }
  }, [joyrideProps]);

  const config: JoyrideProps = useMemo(
    () => ({
      run,
      continuous: true,
      ...joyrideProps,
      styles: {
        ...joyrideProps?.styles,
        options: {
          zIndex: 2000,
          ...joyrideProps?.styles?.options,
        },
        tooltip: {
          boxShadow: theme.shadows[2],
          ...joyrideProps?.styles?.tooltip,
        },
      },
      callback: (data) => {
        if (data.type === EVENTS.TOUR_END) {
          setQueue((currentQueue) => currentQueue.slice(1));
        }
        joyrideProps?.callback?.(data);
      },
      steps: joyrideProps?.steps || [],
    }),
    [joyrideProps, theme, run],
  );

  return (
    <tourGuideContext.Provider value={{ enqueueTourGuide }}>
      <Joyride {...config} />
      {children}
    </tourGuideContext.Provider>
  );
};
export const useTourGuide = () => useContext(tourGuideContext);
export default TourGuideProvider;
